import React, { useState, useCallback, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  addDoc, 
  collection,
  updateDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const AddFoodItemForm = ({ onClose, onSuccess, editingItem = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    imageUrl: '',
    price: {
      half: 0,
      full: 0
    },
    prepTime: '',
    rating: 4.5,
    isPopular: false,
    foodType: 'veg', // veg, non-veg, egg
    discount: 0,
    ingredients: '',
    allergens: '',
    calories: '',
    spiceLevel: 'medium'
  });

  // Optimized form field handlers
  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, category: e.target.value }));
  }, []);

  const handleImageUrlChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, imageUrl: e.target.value }));
  }, []);

  const handlePriceHalfChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      price: { ...prev.price, half: parseFloat(e.target.value) || 0 }
    }));
  }, []);

  const handlePriceFullChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      price: { ...prev.price, full: parseFloat(e.target.value) || 0 }
    }));
  }, []);

  const handlePrepTimeChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, prepTime: e.target.value }));
  }, []);

  const handleRatingChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, rating: parseFloat(e.target.value) || 4.5 }));
  }, []);

  const handleDiscountChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }));
  }, []);

  const handleSpiceLevelChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, spiceLevel: e.target.value }));
  }, []);

  const handlePopularChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, isPopular: e.target.checked }));
  }, []);

  const handleFoodTypeChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, foodType: e.target.value }));
  }, []);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name || '',
        description: editingItem.description || '',
        category: editingItem.category || '',
        imageUrl: editingItem.imageUrl || '',
        price: {
          half: editingItem.price?.half || 0,
          full: editingItem.price?.full || 0
        },
        prepTime: editingItem.prepTime || '',
        rating: editingItem.rating || 4.5,
        isPopular: editingItem.isPopular || false,
        foodType: editingItem.foodType || 'veg',
        discount: editingItem.discount || 0,
        ingredients: editingItem.ingredients || '',
        allergens: editingItem.allergens || '',
        calories: editingItem.calories || '',
        spiceLevel: editingItem.spiceLevel || 'medium'
      });
    }
  }, [editingItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Form submission started...');
      console.log('Form data:', formData);
      
      // Validate required fields
      if (!formData.name || !formData.category) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      const itemData = {
        ...formData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Item data to save:', itemData);

      if (editingItem) {
        console.log('Updating existing item:', editingItem.id);
        await updateDoc(doc(db, 'food_items', editingItem.id), itemData);
        toast.success('Food item updated successfully!');
      } else {
        console.log('Adding new item to Firestore...');
        const docRef = await addDoc(collection(db, 'food_items'), itemData);
        console.log('New item added with ID:', docRef.id);
        toast.success('Food item added successfully!');
      }

      console.log('🔄 Calling onSuccess callback...');
      onSuccess();
      console.log('🚪 Calling onClose callback...');
      onClose();
    } catch (error) {
      console.error('Error saving food item:', error);
      toast.error(`Failed to save food item: ${error.message}`);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            {editingItem ? 'Edit Food Item' : 'Add New Food Item'}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Food Name *
              </Label>
              <Input
                type="text"
                placeholder="Enter food name"
                value={formData.name}
                onChange={handleNameChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Category *
              </Label>
              <select
                value={formData.category}
                onChange={handleCategoryChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="">Select Category</option>
                <option value="main-course">Main Course</option>
                <option value="appetizers">Appetizers</option>
                <option value="desserts">Desserts</option>
                <option value="beverages">Beverages</option>
                <option value="soups">Soups</option>
                <option value="salads">Salads</option>
                <option value="breads">Breads</option>
                <option value="rice">Rice</option>
                <option value="curries">Curries</option>
                <option value="snacks">Snacks</option>
                <option value="sweets">Sweets</option>
              </select>
            </div>
          </div>

          <div>
            <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
              Description *
            </Label>
            <textarea
              placeholder="Enter food description"
              value={formData.description}
              onChange={handleDescriptionChange}
              required
              rows={3}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                width: '100%',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div>
            <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
              Image URL
            </Label>
            <Input
              type="url"
              placeholder="Enter image URL"
              value={formData.imageUrl}
              onChange={handleImageUrlChange}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                width: '100%',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Half Portion Price *
              </Label>
              <Input
                type="number"
                placeholder="Enter half portion price"
                value={formData.price.half}
                onChange={handlePriceHalfChange}
                required
                min="0"
                step="0.01"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Full Portion Price *
              </Label>
              <Input
                type="number"
                placeholder="Enter full portion price"
                value={formData.price.full}
                onChange={handlePriceFullChange}
                required
                min="0"
                step="0.01"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Food Type *
              </Label>
              <select
                value={formData.foodType}
                onChange={handleFoodTypeChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="veg">🟢 Vegetarian</option>
                <option value="non-veg">🔴 Non-Vegetarian</option>
                <option value="egg">🟡 Contains Egg</option>
              </select>
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Popular Item
              </Label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '2px solid #e5e7eb' }}>
                <input
                  type="checkbox"
                  checked={formData.isPopular}
                  onChange={handlePopularChange}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                  Mark as Popular
                </span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              style={{
                padding: '12px 24px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Save style={{ width: '16px', height: '16px' }} />
              {editingItem ? 'Update Item' : 'Save Item'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFoodItemForm;
